/**
 * 좌표 변환 순수 유틸 (M15 / A10).
 *
 * 핵심: 사용자가 보는 화면의 마우스 px → PNG 자연 픽셀(200 DPI) 로 변환.
 * 이미지가 줌/리사이즈되어 화면 표시 크기(displayWidth)가 달라져도, overlay layer
 * 의 bounding rect 와 naturalWidth 비율로 보정하므로 항상 자연 px 로 저장된다.
 * (M11 BboxOverlay 와 동일 좌표계 — 서버 burn-in 과 일치.)
 */

import type { Bbox, Point } from "./types";

/**
 * overlay layer 내부 클라이언트 좌표 → 자연 픽셀.
 *
 * @param clientX 마우스 clientX
 * @param clientY 마우스 clientY
 * @param rect    overlay layer 의 getBoundingClientRect()
 * @param naturalW 이미지 natural width
 * @param naturalH 이미지 natural height
 */
export function clientToNatural(
  clientX: number,
  clientY: number,
  rect: { left: number; top: number; width: number; height: number },
  naturalW: number,
  naturalH: number,
): Point {
  const scaleX = rect.width > 0 ? naturalW / rect.width : 1;
  const scaleY = rect.height > 0 ? naturalH / rect.height : 1;
  const x = Math.round((clientX - rect.left) * scaleX);
  const y = Math.round((clientY - rect.top) * scaleY);
  return {
    x: clamp(x, 0, naturalW),
    y: clamp(y, 0, naturalH),
  };
}

/** 두 점(자연 px) → 정규화된 Bbox (좌상단 기준 + 양수 폭/높이, 경계 clamp). */
export function rectFromPoints(a: Point, b: Point, naturalW: number, naturalH: number): Bbox {
  const x1 = clamp(Math.min(a.x, b.x), 0, naturalW);
  const y1 = clamp(Math.min(a.y, b.y), 0, naturalH);
  const x2 = clamp(Math.max(a.x, b.x), 0, naturalW);
  const y2 = clamp(Math.max(a.y, b.y), 0, naturalH);
  return {
    x: x1,
    y: y1,
    width: Math.max(1, x2 - x1),
    height: Math.max(1, y2 - y1),
  };
}

/** Bbox(자연 px) → overlay 내 % CSS (BboxOverlay 와 동일 규칙). */
export function bboxToPercentStyle(
  bbox: Bbox,
  naturalW: number,
  naturalH: number,
): { left: string; top: string; width: string; height: string } {
  return {
    left: `${(bbox.x / naturalW) * 100}%`,
    top: `${(bbox.y / naturalH) * 100}%`,
    width: `${(bbox.width / naturalW) * 100}%`,
    height: `${(bbox.height / naturalH) * 100}%`,
  };
}

/** 최소 드래그 거리 미만이면 "클릭"으로 간주 (실수 박스 방지). */
export function isMeaningfulBox(bbox: Bbox, minPx = 4): boolean {
  return bbox.width >= minPx && bbox.height >= minPx;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
